import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { Finding, Severity, Target } from '@shared/protocol';
import { SeverityBadge } from './SummaryPanel';
import { Panel, Segmented } from './Panel';

const order: Record<Severity, number> = { FAIL: 0, WARNING: 1, INFO: 2, PASS: 3 };
const time = (t: number) => new Date(t).toLocaleTimeString('en-GB', { hour12: false });

function duration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  return m < 60 ? `${m} min ${s % 60} s` : `${Math.floor(m / 60)} h ${m % 60} min`;
}

function FindingRow({ f, targets }: { f: Finding; targets: Target[] }) {
  const [open, setOpen] = useState(false);
  const resolved = f.resolvedAt !== null;
  const target = f.targetId !== null ? targets.find((t) => t.id === f.targetId) : null;
  return (
    <li className={`finding ${resolved ? 'finding-resolved' : ''}`}>
      <button type="button" className="finding-head" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {open ? <ChevronDown size={16} aria-hidden /> : <ChevronRight size={16} aria-hidden />}
        <SeverityBadge severity={f.severity} />
        <span className="finding-title">{f.title}</span>
        <span className="finding-meta muted small">
          {f.severity === 'PASS'
            ? 'current status'
            : resolved
              ? `resolved at ${time(f.resolvedAt!)}`
              : `since ${time(f.startedAt)} (${duration(Date.now() - f.startedAt)})`}
        </span>
      </button>
      {open && (
        <div className="finding-body">
          <p>{f.explanation}</p>
          {f.evidence.length > 0 && (
            <dl className="kv">
              {f.evidence.map((e) => (
                <div key={e.label} style={{ display: 'contents' }}>
                  <dt>{e.label}</dt>
                  <dd>{e.value}</dd>
                </div>
              ))}
            </dl>
          )}
          {f.likelyCauses.length > 0 && (
            <>
              <h4>Likely causes</h4>
              <ul>
                {f.likelyCauses.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </>
          )}
          {f.suggestedFix && (
            <>
              <h4>Suggested fix</h4>
              <p>{f.suggestedFix}</p>
            </>
          )}
          <p className="muted small">
            Confidence: {f.confidence}. Category: {f.category}.{target ? ` Applies to ${target.name}.` : ''} Started {time(f.startedAt)}
            {resolved ? `, resolved ${time(f.resolvedAt!)}` : ''}.
          </p>
        </div>
      )}
    </li>
  );
}

type Filter = 'open' | 'all';

export function FindingsList({ findings, targets }: { findings: Finding[]; targets: Target[] }) {
  const [filter, setFilter] = useState<Filter>('open');
  const list = findings
    .filter((f) => (filter === 'open' ? f.resolvedAt === null : true))
    .sort((a, b) => (a.resolvedAt === null ? 0 : 1) - (b.resolvedAt === null ? 0 : 1) || order[a.severity] - order[b.severity] || b.startedAt - a.startedAt);
  const openCount = findings.filter((f) => f.resolvedAt === null && f.severity !== 'PASS').length;
  return (
    <Panel
      title={`Findings${openCount ? ` (${openCount} open)` : ''}`}
      actions={
        <Segmented<Filter>
          label="Findings shown"
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'open', label: 'Current' },
            { value: 'all', label: 'Include resolved' },
          ]}
        />
      }
    >
      {list.length === 0 ? (
        <p className="muted">{filter === 'open' ? 'No current findings.' : 'No findings yet.'}</p>
      ) : (
        <ul className="findings">
          {list.map((f) => (
            <FindingRow key={`${f.ruleId}|${f.key}|${f.id}|${f.startedAt}`} f={f} targets={targets} />
          ))}
        </ul>
      )}
    </Panel>
  );
}
